<template>
  <PropertyNotFound v-if="isMissing" />

  <div v-else-if="property" class="property-container" @click.capture="guardManageAction">
    <!-- A unit of a building says where it sits, whatever tab is open -->
    <UnitBreadcrumb
      v-if="property.buildingId"
      :building-id="property.buildingId"
      :unit-number="property.location?.apartmentNumber ?? null"
    />
    <RouterView />
  </div>
</template>

<style scoped lang="scss">
.property-container {
  height: 100%;
}
</style>

<script setup lang="ts">
import { computed, watch, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAvailabilityStore } from '@tallo/investor/availability';
import { AppRouteNames, propertyDashboardRoute, propertyDetailsRoute } from '@tallo/investor/navigation';
import {
  PropertyNotFound,
  useManageActionGuard,
  usePropertyInquiriesQuery,
  usePropertyStore,
} from '@tallo/investor/property';
import { PropertyDto, PropertyStatus } from '@tallo/property';

import UnitBreadcrumb from './components/unit-breadcrumb.vue';

const route = useRoute();
const router = useRouter();
const propertyStore = usePropertyStore();
const availabilityStore = useAvailabilityStore();
const propertyInquiriesQuery = usePropertyInquiriesQuery();
// Read from the route every time: moving from one unit's page to another's keeps this component
const propertyId = computed(() => route.params.propertyId as string | undefined);
const property = computed(() => propertyStore.property);
const isMissing = computed(() => propertyStore.isMissing(propertyId.value));
// Buttons that start a change are stopped for a colleague who can only view the unit
const guardManageAction = useManageActionGuard(() => propertyId.value);

function redirectToCorrectPage(property: PropertyDto): void {
  const propertyId = route.params.propertyId as string;

  const routeTo = isPropertyActive(property) ? propertyDashboardRoute(propertyId) : propertyDetailsRoute(propertyId);

  router.replace(routeTo);
}

function needRedirect(): boolean {
  return router.currentRoute.value.name === AppRouteNames.INVESTOR_PROPERTY;
}

function isPropertyActive(property: PropertyDto): boolean {
  return [PropertyStatus.LISTED, PropertyStatus.LISTING_IN_PROGRESS].includes(property.status);
}

watch(
  propertyId,
  (newPropertyId, oldPropertyId) => {
    if (!newPropertyId || newPropertyId === oldPropertyId) {
      return;
    }

    propertyStore.loadProperty(newPropertyId);
    availabilityStore.loadAvailability(newPropertyId);
    propertyInquiriesQuery.refresh();
  },
  { immediate: true },
);

watch(
  property,
  () => {
    if (property.value) {
      if (needRedirect()) {
        redirectToCorrectPage(property.value);
      }
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  propertyStore.$reset();
  availabilityStore.$reset();
});
</script>
